import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  Grid,
  Chip,
  makeStyles,
  Theme,
  CircularProgress,
  Link,
  Box,
  Paper,
  TextField,
  useTheme,
} from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpIcon from '@material-ui/icons/Help';
import BusinessIcon from '@material-ui/icons/Business';
import {
  fetchConsolidatedGroupTerms,
  updateConsolidatedGroupCancellation,
} from 'lib/Api/TermsGenerator';
import { ConsolidatedGroupTerm } from 'lib/Model/TermsGenerator';
import {
  TermsDocument,
  UpdateTermsDetailsRequest,
} from 'lib/Model/TermsGenerator';
import { Alert } from '@material-ui/lab';
import { DatePicker } from 'components/DatePicker/DatePicker';
import { updateTermsDetails } from 'lib/Api/TermsGenerator';
import { format, parseISO } from 'date-fns';
import { getUserData } from 'lib/Helper/Auth';
import { TermsChangelog } from './TermsChangelog';

const useStyles = makeStyles((theme: Theme) => ({
  contentGrid: {
    marginTop: theme.spacing(1),
  },
  label: {
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
  },
  statusActive: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    fontWeight: 'bold',
  },
  statusExpired: {
    backgroundColor: theme.palette.error.light,
    color: 'black',
    fontWeight: 'bold',
  },
  statusPending: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    fontWeight: 'bold',
  },
  statusCancelled: {
    backgroundColor: theme.palette.grey[400],
    fontWeight: 'bold',
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },
  statusChip: {
    fontSize: '0.9rem',
  },
  statusIcon: {
    fontSize: '1rem',
    color: 'black',
  },
  cancelButton: {
    color: theme.palette.error.main,
  },
  cancelSection: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  folderLink: {
    display: 'flex',
    alignItems: 'center',
  },
  datesPaper: {
    padding: theme.spacing(2),
  },
  errorAlert: {
    marginTop: theme.spacing(2),
  },
  warningAlert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  permissionMessage: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  expiryHelperMessage: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderLeft: `4px solid ${theme.palette.info.main}`,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  commentField: {
    marginTop: theme.spacing(1),
  },
  actionsSpacer: {
    flexGrow: 1,
  },
  boldText: {
    fontWeight: 'bold',
  },
}));

interface TermsDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  term?: TermsDocument;
  onSaved: () => void;
}

export const TermsDetailsDialog: React.FC<TermsDetailsDialogProps> = ({
  open,
  onClose,
  term,
  onSaved,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [subscriptionDate, setSubscriptionDate] = useState<Date | null>(null);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [signatureDate, setSignatureDate] = useState<Date | null>(null);
  const [comment, setComment] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [pendingPayload, setPendingPayload] =
    useState<UpdateTermsDetailsRequest | null>(null);
  const [datesModified, setDatesModified] = useState(false);
  const [zOpen, setZOpen] = useState(false);
  const [rOpen, setROpen] = useState(false);
  const [groupedCompanies, setGroupedCompanies] = useState<
    ConsolidatedGroupTerm[]
  >([]);
  const [isLoadingGroup, setIsLoadingGroup] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [cancelScopeDialogOpen, setCancelScopeDialogOpen] = useState(false);
  const [reinstateScopeDialogOpen, setReinstateScopeDialogOpen] =
    useState(false);

  const user = getUserData();
  const permissions = user?.permissions ?? [];
  const hasEditTermsPermission = permissions.includes('edit_terms');
  const isCancelled = Boolean(term?.is_cancelled);

  useEffect(() => {
    if (open && term) {
      setSubscriptionDate(
        term.subscription_date ? parseISO(term.subscription_date) : null,
      );
      setExpiryDate(
        term.terms_expiry_date ? parseISO(term.terms_expiry_date) : null,
      );
      setSignatureDate(
        term.signature_date ? parseISO(term.signature_date) : null,
      );
      setComment(term.comment || '');
      setHasChanges(false);
      setIsSaving(false);
      setError(null);
      setDatesModified(false);
    } else if (!open) {
      setSubscriptionDate(null);
      setExpiryDate(null);
      setSignatureDate(null);
      setComment('');
      setHasChanges(false);
      setIsSaving(false);
      setError(null);
      setDatesModified(false);
    }
  }, [term, open]);

  useEffect(() => {
    const fetchGroupData = async () => {
      if (term?.consolidated_group_id) {
        setIsLoadingGroup(true);
        setGroupError(null);
        try {
          const data = await fetchConsolidatedGroupTerms(
            term.consolidated_group_id,
          );
          setGroupedCompanies(data);
        } catch (err) {
          setGroupError('Failed to load other companies in this group.');
        } finally {
          setIsLoadingGroup(false);
        }
      }
    };

    if (open) {
      fetchGroupData();
    } else {
      setGroupedCompanies([]);
    }
  }, [open, term]);

  if (!term) {
    return null;
  }

  let status = 'Unknown';
  let statusClass = '';
  let now = new Date();

  if (isCancelled) {
    status = 'Cancelled';
    statusClass = classes.statusCancelled;
  } else if (!term.signature_date) {
    status = 'Pending Signature';
    statusClass = classes.statusPending;
  } else if (term.signature_date) {
    const signatureDate = new Date(term.signature_date);

    if (term.terms_expiry_date) {
      const expiryDate = new Date(term.terms_expiry_date);

      if (now > expiryDate) {
        status = 'Expired';
        statusClass = classes.statusExpired;
      } else if (now >= signatureDate) {
        status = 'Active';
        statusClass = classes.statusActive;
      } else {
        status = 'Not yet in effect';
        statusClass = classes.statusPending;
      }
    } else {
      if (now >= signatureDate) {
        status = 'Active';
        statusClass = classes.statusActive;
      } else {
        status = 'Not yet in effect';
        statusClass = classes.statusPending;
      }
    }
  }

  const handleDateChange =
    (setter: React.Dispatch<React.SetStateAction<Date | null>>) =>
    (date: Date | null) => {
      if (!hasEditTermsPermission) return;

      setter(date);
      setHasChanges(true);
      setDatesModified(true);
      setError(null);
    };

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!hasEditTermsPermission) return;

    setComment(event.target.value);
    setHasChanges(true);
    setError(null);
  };

  const handleSaveChanges = async () => {
    if (!term) return;

    const payload: UpdateTermsDetailsRequest = {};
    if (
      subscriptionDate !==
      (term.subscription_date ? parseISO(term.subscription_date) : null)
    ) {
      payload.subscriptionDate = subscriptionDate
        ? format(subscriptionDate, 'yyyy-MM-dd')
        : undefined;
    }
    if (
      expiryDate !==
      (term.terms_expiry_date ? parseISO(term.terms_expiry_date) : null)
    ) {
      payload.expiryDate = expiryDate
        ? format(expiryDate, 'yyyy-MM-dd')
        : undefined;
    }
    if (
      signatureDate !==
      (term.signature_date ? parseISO(term.signature_date) : null)
    ) {
      payload.signatureDate = signatureDate
        ? format(signatureDate, 'yyyy-MM-dd')
        : undefined;
    }
    if (comment !== (term.comment || '')) {
      payload.comment = comment;
    }
    payload.isCancelled = isCancelled;

    const isConsolidated = !!term.consolidated_group_id;
    const isSyncFieldChanged =
      payload.signatureDate !== undefined ||
      payload.expiryDate !== undefined ||
      payload.comment !== undefined;

    if (isConsolidated && isSyncFieldChanged) {
      setPendingPayload(payload);
      setConfirmSyncOpen(true);
    } else {
      await executeSaveChanges(payload);
    }
  };

  const executeSaveChanges = async (payload: UpdateTermsDetailsRequest) => {
    if (!term) return;
    setIsSaving(true);
    setError(null);

    if (Object.keys(payload).length > 0) {
      try {
        await updateTermsDetails(term.id, payload);
        setHasChanges(false);
        onSaved();
        onClose();
      } catch (err) {
        setError('Failed to save changes. Please try again.');
      } finally {
        setIsSaving(false);
        setConfirmSyncOpen(false);
        setPendingPayload(null);
      }
    } else {
      setHasChanges(false);
      setIsSaving(false);
    }
  };

  const handleCancelTerms = async () => {
    if (!term) return;
    setIsSaving(true);
    setError(null);

    const payload: UpdateTermsDetailsRequest = {};
    if (
      subscriptionDate !==
      (term.subscription_date ? parseISO(term.subscription_date) : null)
    ) {
      payload.subscriptionDate = subscriptionDate
        ? format(subscriptionDate, 'yyyy-MM-dd')
        : undefined;
    }
    if (
      expiryDate !==
      (term.terms_expiry_date ? parseISO(term.terms_expiry_date) : null)
    ) {
      payload.expiryDate = expiryDate
        ? format(expiryDate, 'yyyy-MM-dd')
        : undefined;
    }
    if (
      signatureDate !==
      (term.signature_date ? parseISO(term.signature_date) : null)
    ) {
      payload.signatureDate = signatureDate
        ? format(signatureDate, 'yyyy-MM-dd')
        : undefined;
    }
    if (comment !== (term.comment || '')) {
      payload.comment = comment;
    }
    payload.isCancelled = true;

    try {
      await updateTermsDetails(term.id, payload);
      setHasChanges(false);
      onSaved();
      setCancelDialogOpen(false);
      onClose();
    } catch (err) {
      setError('Failed to cancel the agreement. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const doRevert = async () => {
    if (!term) return;
    setIsSaving(true);
    setError(null);

    try {
      await updateTermsDetails(term.id, { isCancelled: false });
      setHasChanges(false);
      onSaved();
      setROpen(false);
      onClose();
    } catch (err) {
      setError('Failed to reinstate the agreement. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGroupAction = async (cancelAction: boolean) => {
    if (!term?.consolidated_group_id) return;
    setIsSaving(true);
    setError(null);
    try {
      await updateConsolidatedGroupCancellation(
        term.consolidated_group_id,
        cancelAction,
      );
      onSaved();
      setCancelScopeDialogOpen(false);
      setReinstateScopeDialogOpen(false);
      onClose();
    } catch (err) {
      setError(
        `Failed to ${
          cancelAction ? 'cancel' : 'reinstate'
        } the group agreement. Please try again.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Agreement Details: {term.company_name} ({term.terms_type})
          <Typography variant="subtitle2" color="textSecondary">
            Document ID: {term.id}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box mb={3} pb={2} borderBottom={1} borderColor="divider">
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="body1" className={classes.label}>
                  Status:
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={status}
                  size="small"
                  className={`${classes.statusChip} ${statusClass}`}
                  icon={
                    status === 'Active' ? (
                      <CheckCircleIcon className={classes.statusIcon} />
                    ) : status === 'Expired' ? (
                      <ErrorIcon className={classes.statusIcon} />
                    ) : status === 'Pending Signature' ||
                      status === 'Not yet in effect' ? (
                      <HourglassEmptyIcon className={classes.statusIcon} />
                    ) : status === 'Cancelled' ? (
                      <CancelIcon className={classes.statusIcon} />
                    ) : (
                      <HelpIcon className={classes.statusIcon} />
                    )
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {term.consolidated_group_id && (
            <Box mb={3} pb={2} borderBottom={1} borderColor="divider">
              <Typography
                variant="h6"
                className={classes.sectionTitle}
                gutterBottom
              >
                Consolidated Group Companies
              </Typography>
              {isLoadingGroup ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : groupError ? (
                <Alert severity="error">{groupError}</Alert>
              ) : (
                <Paper variant="outlined">
                  <Box maxHeight={150} overflow="auto">
                    {groupedCompanies.map((groupTerm) => (
                      <Box
                        key={groupTerm.id}
                        px={2}
                        py={1}
                        style={{
                          backgroundColor:
                            groupTerm.id === term.id
                              ? theme.palette.action.hover
                              : 'transparent',
                          borderBottom: '1px solid ' + theme.palette.divider,
                        }}
                      >
                        <Grid container alignItems="center">
                          <Grid item>
                            <BusinessIcon
                              fontSize="small"
                              style={{ marginRight: theme.spacing(1) }}
                            />
                          </Grid>
                          <Grid item xs>
                            {groupTerm.company_id ? (
                              <Link
                                href={`${window.location.origin}/clients/${groupTerm.company_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                                variant="body2"
                              >
                                {groupTerm.company_name}
                              </Link>
                            ) : (
                              <Typography variant="body2">
                                {groupTerm.company_name}
                              </Typography>
                            )}
                          </Grid>
                          {groupTerm.id === term.id && (
                            <Grid item>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                (Current)
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          <Typography
            variant="h6"
            className={classes.sectionTitle}
            gutterBottom
          >
            Agreement Information
          </Typography>
          <Grid container spacing={2} className={classes.contentGrid}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <span className={classes.label}>Company:</span>
                {term.company_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <span className={classes.label}>Agreement Type:</span>
                {term.terms_type}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <span className={classes.label}>Brand:</span>
                {term.terms_brand}
              </Typography>
            </Grid>
            {term.folder_id && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" className={classes.folderLink}>
                  <span className={classes.label}>Folder:</span>
                  <Link
                    href={`https://drive.google.com/drive/folders/${term.folder_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    className={classes.folderLink}
                  >
                    <FolderIcon fontSize="small" />
                    View in Drive
                  </Link>
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box mt={3} mb={2}>
            <Paper variant="outlined" className={classes.datesPaper}>
              <Grid container spacing={2}>
                {term.terms_brand !== 'Konnekt' && (
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Subscription Date"
                      value={subscriptionDate}
                      onChange={handleDateChange(setSubscriptionDate)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      clearable
                      inputVariant="outlined"
                      size="small"
                      margin="dense"
                      disabled={
                        term.is_cancelled ||
                        !hasEditTermsPermission ||
                        !!term.consolidated_group_id
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={term.terms_brand === 'Konnekt' ? 6 : 4}>
                  <DatePicker
                    label="Signature Date"
                    value={signatureDate}
                    onChange={handleDateChange(setSignatureDate)}
                    fullWidth
                    clearable
                    InputLabelProps={{ shrink: true }}
                    inputVariant="outlined"
                    size="small"
                    margin="dense"
                    disabled={term.is_cancelled || !hasEditTermsPermission}
                  />
                </Grid>
                <Grid item xs={12} md={term.terms_brand === 'Konnekt' ? 6 : 4}>
                  <DatePicker
                    label="Expiry Date"
                    value={expiryDate}
                    onChange={handleDateChange(setExpiryDate)}
                    fullWidth
                    clearable
                    InputLabelProps={{ shrink: true }}
                    inputVariant="outlined"
                    size="small"
                    margin="dense"
                    disabled={term.is_cancelled || !hasEditTermsPermission}
                  />
                </Grid>
              </Grid>
              {term.terms_brand === 'SIM' && (
                <Box className={classes.expiryHelperMessage}>
                  <Typography variant="body2">
                    The expiry date should be calculated as one year from the{' '}
                    <span className={classes.boldText}>subscription date</span>.
                  </Typography>
                </Box>
              )}
            </Paper>
            {!hasEditTermsPermission ? (
              <Alert severity="info" className={classes.permissionMessage}>
                You don't have permission to modify agreement details.
              </Alert>
            ) : (
              datesModified && (
                <Alert severity="info" className={classes.warningAlert}>
                  Note: Modifying dates here will not update the actual document
                  content.
                </Alert>
              )
            )}
          </Box>

          <Box mt={3} className={classes.commentField}>
            <TextField
              label="Comments"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={comment}
              onChange={handleCommentChange}
              InputLabelProps={{ shrink: true }}
              disabled={term.is_cancelled || !hasEditTermsPermission}
              placeholder="Add any notes or comments about this agreement"
              helperText={
                !hasEditTermsPermission
                  ? "You don't have permission to add comments"
                  : ''
              }
            />
          </Box>

          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <span className={classes.label}>Created On:</span>
                  {term.created_on
                    ? format(new Date(term.created_on), 'dd/MM/yyyy')
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <span className={classes.label}>Created By:</span>
                  {term.created_by}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" className={classes.errorAlert}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!isCancelled && hasEditTermsPermission && (
            <Button
              className={classes.cancelButton}
              onClick={() => {
                if (term.consolidated_group_id) {
                  setCancelScopeDialogOpen(true);
                } else {
                  setCancelDialogOpen(true);
                }
              }}
              disabled={isSaving}
              variant="text"
            >
              Cancel Agreement
            </Button>
          )}
          {isCancelled && hasEditTermsPermission && (
            <Button
              onClick={() => {
                if (term.consolidated_group_id) {
                  setReinstateScopeDialogOpen(true);
                } else {
                  setROpen(true);
                }
              }}
              color="primary"
              disabled={isSaving}
              variant="text"
            >
              Reinstate Agreement
            </Button>
          )}
          <Button
            onClick={() => setZOpen(true)}
            color="primary"
            disabled={isSaving}
          >
            View History
          </Button>
          <Box className={classes.actionsSpacer} />
          <Button onClick={onClose} color="primary" disabled={isSaving}>
            Close
          </Button>
          {hasEditTermsPermission && (
            <Button
              onClick={handleSaveChanges}
              color="primary"
              variant="contained"
              disabled={!hasChanges || isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <TermsChangelog
        open={zOpen}
        onClose={() => setZOpen(false)}
        termsId={term.id}
      />

      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're about to cancel this agreement. This action is usually taken
            when the client has stopped responding or either party decides not
            to proceed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} color="primary">
            No, Keep Active
          </Button>
          <Button
            onClick={handleCancelTerms}
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Processing...' : 'Yes, Cancel Agreement'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rOpen}
        onClose={() => setROpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Reinstatement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're about to reinstate this agreement. This will mark it as
            active again and allow edits.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setROpen(false)} color="primary">
            No, Keep Cancelled
          </Button>
          <Button
            onClick={doRevert}
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Processing...' : 'Reinstate Agreement'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmSyncOpen}
        onClose={() => setConfirmSyncOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Group Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is a consolidated agreement. Updating the{' '}
            <span className={classes.boldText}>
              Signature Date, Expiry Date, or Comments
            </span>{' '}
            will apply the same changes to all other agreements in this group.
            <br />
            <br />
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSyncOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingPayload) {
                executeSaveChanges(pendingPayload);
              }
            }}
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Yes, Update All'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelScopeDialogOpen}
        onClose={() => setCancelScopeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Cancellation Scope</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This agreement is part of a consolidated group. Do you want to
            cancel only this specific agreement for{' '}
            <span className={classes.boldText}>{term.company_name}</span>, or
            cancel the agreement for all companies in the group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelScopeDialogOpen(false)}
            color="primary"
            disabled={isSaving}
          >
            Back
          </Button>
          <Button
            onClick={handleCancelTerms}
            color="primary"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            Cancel This One Only
          </Button>
          <Button
            onClick={() => handleGroupAction(true)}
            color="secondary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            Cancel All in Group
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reinstateScopeDialogOpen}
        onClose={() => setReinstateScopeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Reinstatement Scope</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This agreement is part of a consolidated group. Do you want to
            reinstate only this specific agreement for{' '}
            <span className={classes.boldText}>{term.company_name}</span>, or
            reinstate the agreement for all companies in the group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReinstateScopeDialogOpen(false)}
            color="primary"
            disabled={isSaving}
          >
            Back
          </Button>
          <Button
            onClick={doRevert}
            color="primary"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            Reinstate This One Only
          </Button>
          <Button
            onClick={() => handleGroupAction(false)}
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            Reinstate All in Group
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export {};
